import { CSSProperties, PropsWithChildren, ReactNode } from 'react';

import {
  Divider,
  Grid,
  Stack,
  StackProps,
  Typography,
  styled,
} from '@mui/material';
import clsx from 'clsx';

import { colors } from '@/theme/variables';

const FormCardRoot = styled(Stack)(({ theme }) => ({
  position: 'relative',
  width: 'calc(100% - 2px)',
  margin: '12px 1px 1px 1px',
  backgroundColor: 'white',
  borderRadius: 8,
  border: '1px solid #E7EAEC',
  ...(theme.palette.mode !== 'light' && {
    backgroundColor: colors.grey[80],
    border: '1px solid rgba(255, 255, 255, 0.1)',
  }),

  '&:first-of-type': {
    marginTop: 0,
  },
  '& .cardTitle': {
    fontWeight: 500,
    padding: '12px 15px',
  },
  '& .cardContent': {
    marginLeft: '-16px',
    padding: '12px 15px',
  },
  '& .divider': {
    marginTop: '12px',
  },

  '&.kaseScrollableCard': {
    '&.kaseFormCard': {
      '.MuiTableCell-head': {
        paddingTop: 0,
      },
      '.cardContent': {
        marginTop: 0,
      },
    },
    '.cardContent': {
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    '.MuiAutocomplete-root': {
      position: 'sticky',
      top: 0,
    },
  },

  '&.grey-title': {
    '.cardTitle': {
      backgroundColor: theme.palette.background.formHeader,
      padding: '14px 10px',
      width: '100%',
      fontWeight: 500,
      fontSize: '1rem',
      borderRadius: '8px 8px 0 0',
    },
    '.divider': {
      display: 'none',
    },
  },
  '&.light-red-title': {
    '.cardTitle': {
      backgroundColor: 'rgba(238, 62, 92, 0.4)',
      padding: '14px 10px',
      width: '100%',
      fontWeight: 500,
      borderRadius: '8px 8px 0 0',
    },
    '.divider': {
      display: 'none',
    },
  },
}));

interface FormCardProps extends PropsWithChildren {
  title?: string | ReactNode;
  style?: CSSProperties;
  classes?: { cardContainer?: string; cardContent?: string };
  className?: string;
  sx?: StackProps['sx'];
}

export const FormCard = ({
  title,
  classes,
  children,
  style,
  sx,
  className,
}: FormCardProps) => {
  return (
    <FormCardRoot
      spacing={1.5}
      divider={title ? <Divider className="divider" /> : ''}
      className={clsx(classes?.cardContainer, className)}
      style={style}
      sx={sx}
    >
      {title && typeof title === 'string' ? (
        <Typography variant="inherit" className="cardTitle">
          {title}
        </Typography>
      ) : (
        title
      )}
      <Grid
        columnSpacing={2}
        container
        className={clsx('cardContent', classes?.cardContent)}
      >
        {children}
      </Grid>
    </FormCardRoot>
  );
};
